import React, { useState } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import Snackbar from '@mui/material/Snackbar';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

// Contexts
import { withTranslate } from '../../core/TenantProvider';
// Files in the same folder
import { TrackingCodeSection } from './TrackingCodeSection';
import TrackingCodeContainerStyles from './styles/TrackingCodeContainer.style';
import { ReactComponent as SVGTickIcon } from './assets/icons/icon-tick-circle.svg';
import { ReactComponent as SVGCloseIcon } from './assets/icons/icon-close.svg';
// Types
import { Affiliate, AffiliateDetailsAttributes } from '../../core/types/AffiliateDetails';
// Redux
import { ReducerState } from '../../redux/modules';
// Constants
import {
    CD,
    ORGANIZATION_CODE_URL_MAPPING
} from '../../core/utils/Constants/Constants';

interface TrackingCodeContainerProps extends WithStyles<typeof TrackingCodeContainerStyles> {
    translate: Function;
    affiliateDetails: AffiliateDetailsAttributes;
    componentData: any;
    launchDarklyFlags: LDFlagSet
}

export interface LanguageFlagsData {
    lang: string;
    flagName: string;
    flag: string;
}

type TrackingCodeURLType = {
    clients: LanguageFlagsData[];
    partners?: LanguageFlagsData[];
    directSignUp: LanguageFlagsData[];
}

const TrackingCodeContainer = (props: TrackingCodeContainerProps) => {
    const {
        classes,
        translate,
        affiliateDetails,
        componentData,
        launchDarklyFlags = {}
    } = props;

    const [copySuccess, setCopySuccess] = useState<boolean>(false);

    const flagComponents = ({ flagName }) => {
        let flagIcon = '';
        if (flagName) {
            flagIcon = require(`../../core/assets/CountriesList/countries/${flagName}.svg`);
        }
        return (
            <span>
                <img src={flagIcon} alt="flag Icon" />
            </span>
        );
    };

    const handleClose = (e?: React.SyntheticEvent<any, Event> | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setCopySuccess(false);
    };

    const handleCopySuccess = () => {
        setCopySuccess(true);
    };

    const affiliate: Affiliate | undefined = affiliateDetails?.affiliate;
    const affiliateNumber: string = affiliate?.affiliate_Number;

    let data: TrackingCodeURLType;
    let jsonFileName = ORGANIZATION_CODE_URL_MAPPING[componentData.orgCode];
    if (!jsonFileName) {
        jsonFileName = ORGANIZATION_CODE_URL_MAPPING[CD];
    }
    const jsonFileData = require(`./reference/${jsonFileName}.json`);
    data = jsonFileData.languages;

    return (
        <div className={classes.trackingCodeContainer}>
            {copySuccess && (
                <Snackbar
                    className={classNames(classes.APSnackBar, classes.snackBarWidth)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={copySuccess}
                    autoHideDuration={3000}
                    onClose={handleClose}
                >
                    <div>
                        <SVGTickIcon className={classes.svgTick} />
                        {translate('trackingCode.copycode.success')}
                        <SVGCloseIcon
                            className={classes.svgIconClose}
                            onClick={handleClose}
                        />
                    </div>
                </Snackbar>
            )}
            <TrackingCodeSection
                header={translate('trackingCode.details.client.links.header')}
                linkType="client"
                data={data.clients}
                componentData={componentData}
                affiliateNumber={affiliateNumber}
                classes={classes}
                translate={translate}
                onCopySuccess={handleCopySuccess}
                flagComponents={flagComponents}
            />
            {data.partners && !affiliate.disable_partner_referrals && (
                <TrackingCodeSection
                    header={translate('trackingCode.details.partner.links.header')}
                    linkType="partner"
                    data={data.partners}
                    componentData={componentData}
                    affiliateNumber={affiliateNumber}
                    classes={classes}
                    translate={translate}
                    onCopySuccess={handleCopySuccess}
                    flagComponents={flagComponents}
                />
            )}
            <TrackingCodeSection
                header={translate('trackingCode.details.directSignUp.links.header')}
                linkType="directSignUp"
                data={data.directSignUp}
                componentData={componentData}
                affiliateNumber={affiliateNumber}
                classes={classes}
                translate={translate}
                onCopySuccess={handleCopySuccess}
                flagComponents={flagComponents}
            />
        </div>
    );
}

const mapStateToProps = (store: ReducerState) => ({
    affiliateDetails: store.user.affiliateDetails,
    componentData: store.uiStates.componentData,
    launchDarklyFlags: store.user.launchDarklyFlags
});

export default connect(mapStateToProps, null)(
    withTranslate(withStyles(TrackingCodeContainerStyles)(
        TrackingCodeContainer
    ))
);